<template>
  <UtilInput
    :label="$t('host-name')"
    :iconLink="detectiveHatIcon"
    :placeholder="$t('enter-name')"
    :value="form.hostName"
    @update:value="updateField('hostName', $event)"
  />
  <div :class="formModuleClass">
    <UtilInput
      :label="$t('allowed-players')"
      inputType="number"
      :iconLink="usersIcon"
      :placeholder="$t('enter-total-players')"
      :value="form.allowedPlayers"
      @update:value="updateField('allowedPlayers', $event)"
    />
    <UtilInput
      :label="$t('group-limit')"
      inputType="number"
      :iconLink="usersIcon"
      :placeholder="$t('enter-group-limit')"
      :value="form.groupLimit"
      @update:value="updateField('groupLimit', $event)"
    >
      <template #label-extra>
        <span class="tooltipWrap" tabindex="0" :aria-label="$t('group-limit-optimal-tooltip')">
          <span class="tooltipTrigger">i</span>
          <span class="tooltipBubble">{{ $t("group-limit-optimal-tooltip") }}</span>
        </span>
      </template>
    </UtilInput>
  </div>
  <div :class="formModuleClass">
    <UtilInput
      :label="$t('reading-time')"
      inputType="number"
      :iconLink="emptyHourglassIcon"
      :placeholder="$t('allowed-time')"
      :value="form.readingTime"
      @update:value="updateField('readingTime', $event)"
      :helperText="$t('minutes')"
    />
    <UtilInput
      :label="$t('discussion-time')"
      inputType="number"
      :iconLink="emptyHourglassIcon"
      :placeholder="$t('allowed-time')"
      :value="form.discussionTime"
      @update:value="updateField('discussionTime', $event)"
      :helperText="$t('in-minutes')"
    />
  </div>
  <UtilInput
    :label="$t('prelim-vote-reason-toggle')"
    :placeholder="$t('prelim-vote-reason-desc')"
    :isCheckBox="true"
    :value="form.preliminaryReason"
    @update:value="updateField('preliminaryReason', $event)"
  />
  <UtilInput
    :label="$t('final-vote-reason-toggle')"
    :placeholder="$t('final-vote-reason-desc')"
    :isCheckBox="true"
    :value="form.finalReason"
    @update:value="updateField('finalReason', $event)"
  />
  <slot name="after-reason-toggles" />
  <UtilInput
    :label="$t('discipline')"
    :iconLink="categorizeIcon"
    :placeholder="$t('select-category')"
    :isDropDown="true"
    :dropdownOptions="disciplineOptions"
    :value="form.discipline"
    :isDisabled="disableDiscipline"
    @update:value="updateField('discipline', $event)"
  />
  <UtilInput
    v-if="form.discipline === 'code'"
    :label="$t('language')"
    :iconLink="sourceCodeIcon"
    :placeholder="$t('select-language')"
    :isDropDown="true"
    :dropdownOptions="codeLanguageOptions"
    :value="form.codeLanguage"
    :isDisabled="disableCodeLanguage"
    @update:value="updateField('codeLanguage', $event)"
  />
  <UtilInput
    v-if="showTitleField"
    :label="$t('title')"
    :iconLink="sheetOfPaperIcon"
    :placeholder="$t('enter-title')"
    :helperText="$t('game-title-helper')"
    :value="form.title"
    @update:value="updateField('title', $event)"
  />
  <slot name="before-image" />
  <UtilInput
    v-if="showImageField"
    :label="$t('optional-image')"
    :iconLink="imageIcon"
    :isFileUpload="true"
    accept="image/png,image/jpeg,image/jpg,image/gif"
    :placeholder="imagePlaceholder"
    :buttonText="$t('choose-file')"
    :isDisabled="form.imageUploading"
    :value="form.imageFile"
    :previewUrl="form.imageUrl"
    :previewError="imageUploadFailed"
    :fallbackPreviewIcon="brokenImageIcon"
    :enableImageCompression="true"
    @update:value="updateField('imageFile', $event)"
    @update:previewUrl="updateField('imageUrl', $event)"
  />
</template>

<script>
import UtilInput from "@/components/util/UtilInput.vue";
import DetectiveHatIcon from "@/assets/icons/detective-hat.svg";
import UsersIcon from "@/assets/icons/users.svg";
import EmptyHourglassIcon from "@/assets/icons/empty-hourglass.svg";
import CategorizeIcon from "@/assets/icons/categorize.svg";
import SheetOfPaperIcon from "@/assets/icons/sheet-of-paper.svg";
import SourceCodeIcon from "@/assets/icons/source-code.svg";
import ImageIcon from "@/assets/icons/image.svg";
import BrokenImageIcon from "@/assets/icons/broken-image.svg";

export default {
  name: "GameSetupCoreFields",
  components: {
    UtilInput,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    disciplineOptions: {
      type: Array,
      required: true,
    },
    codeLanguageOptions: {
      type: Array,
      required: true,
    },
    formModuleClass: {
      type: [String, Array, Object],
      required: true,
    },
    imageUploadFailed: {
      type: Boolean,
      default: false,
    },
    showTitleField: {
      type: Boolean,
      default: true,
    },
    showImageField: {
      type: Boolean,
      default: true,
    },
    disableDiscipline: {
      type: Boolean,
      default: false,
    },
    disableCodeLanguage: {
      type: Boolean,
      default: false,
    },
    replaceImagePlaceholderWhenExisting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:form"],
  data() {
    return {
      detectiveHatIcon: DetectiveHatIcon,
      usersIcon: UsersIcon,
      emptyHourglassIcon: EmptyHourglassIcon,
      categorizeIcon: CategorizeIcon,
      sheetOfPaperIcon: SheetOfPaperIcon,
      sourceCodeIcon: SourceCodeIcon,
      imageIcon: ImageIcon,
      brokenImageIcon: BrokenImageIcon,
    };
  },
  computed: {
    imagePlaceholder() {
      const hasExistingImage =
        !!this.form.imageUrl && !this.form.imageUrl.startsWith("blob:");

      if (this.replaceImagePlaceholderWhenExisting && hasExistingImage) {
        return this.$t("replace-image-placeholder");
      }

      return this.$t("no-file-chosen");
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit("update:form", {
        ...this.form,
        [field]: value,
      });
    },
  },
};
</script>

<style scoped>
.tooltipWrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  vertical-align: middle;
}

.tooltipTrigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #d9e6f5;
  color: #112d4e;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: help;
}

.tooltipBubble {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  width: max-content;
  max-width: min(260px, calc(100vw - 40px));
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(17, 45, 78, 0.96);
  color: #fff;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  line-height: 1.35;
  text-align: center;
  box-shadow: 0 10px 24px rgba(17, 45, 78, 0.2);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.16s ease, visibility 0.16s ease;
  z-index: 10;
}

.tooltipBubble::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: rgba(17, 45, 78, 0.96) transparent transparent transparent;
}

.tooltipWrap:hover .tooltipBubble,
.tooltipWrap:focus-within .tooltipBubble {
  opacity: 1;
  visibility: visible;
}
</style>
